import { ChangeEvent, memo, MutableRefObject } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SliderCaptcha, { ActionType, VerifyParam } from 'rc-slider-captcha';

import { SVGIconType } from '../../../../constants/svgIcon';
import {
  getWebPageVariant,
  WebPageType,
} from '../../../../constants/webPageType';
import {
  EmailPhoneTabBar,
  LoadingSpinner,
  SvgIcon,
  TextButton,
  TextButtonSize,
  TextButtonVariant,
  TextInput,
} from '../../../Molecules';

import './LoginTemplate.css';

type TextInputProps = {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

type LoginTemplateProps = {
  webPageType: WebPageType;
  activeTab: 'email' | 'phone';
  onSelectTab: (tab: 'email' | 'phone') => void;
  email: TextInputProps;
  phoneCode: string;
  phoneNumber: TextInputProps;
  password: TextInputProps;
  isClicked: boolean;
  onClick: () => void;
  errorMessage: string;
  isLoading: boolean;
  onClickForget: () => void;
  showCaptcha: boolean;
  verified: boolean;
  onRequest: () => Promise<{
    bgUrl: string;
    puzzleUrl: string;
  }>;
  onVerify: (data: VerifyParam) => Promise<void>;
  actionRef: MutableRefObject<ActionType | undefined>;
  onClickCaptcha: () => void;
  onCloseCaptcha: () => void;
  onClickTryNow: () => void;
  onClickRegister: () => void;
  onClickPhoneCode: () => void;
};

export const LoginTemplate = memo(
  ({
    webPageType,
    activeTab,
    onSelectTab,
    email,
    phoneCode,
    phoneNumber,
    password,
    isClicked,
    onClick,
    errorMessage,
    isLoading,
    onClickForget,
    showCaptcha,
    verified,
    onRequest,
    onVerify,
    actionRef,
    onClickCaptcha,
    onCloseCaptcha,
    onClickRegister,
    onClickTryNow,
    onClickPhoneCode,
  }: LoginTemplateProps) => {
    const { t } = useTranslation();

    return (
      <div className="login">
        <div className="centered-container" onClick={onCloseCaptcha}>
          <p
            className={`text-lg ${
              getWebPageVariant(webPageType).classVariantLight2
            }`}
          >
            {t('Login')}
          </p>
          <EmailPhoneTabBar
            activeTab={activeTab}
            onSelectTab={onSelectTab}
            webPageType={webPageType}
          />
          <div className="login-bar">
            {activeTab === 'email' ? (
              <TextInput
                value={email.value}
                onChange={email.onChange}
                placeholder={t('Email / Username')}
                isClicked={isClicked}
                style={{ backgroundColor: '#f0f0f0' }}
              />
            ) : (
              <TextInput
                value={phoneNumber.value}
                onChange={phoneNumber.onChange}
                placeholder={t('Phone Number')}
                isClicked={isClicked}
                style={{ backgroundColor: '#f0f0f0' }}
                type={'phone'}
                phoneCode={phoneCode}
                onClickPhoneCode={onClickPhoneCode}
              />
            )}
            <TextInput
              value={password.value}
              onChange={password.onChange}
              placeholder={t('Password')}
              isClicked={isClicked}
              type="password"
              style={{ backgroundColor: '#f0f0f0' }}
            />
          </div>
          <div className="forgot-password-container">
            <p
              className={`forgot-password ${
                getWebPageVariant(webPageType).classVariantLight1
              }`}
              onClick={onClickForget}
            >
              {t('Forgotten Password?')}
            </p>
          </div>
          <div
            className="captcha-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="captcha-content">
              <SvgIcon
                className={`captcha-box ${verified ? 'visible' : ''}`}
                name={
                  verified
                    ? SVGIconType.ConfirmTick
                    : SVGIconType.SquareCheckboxEmpty
                }
                width={24}
                height={24}
                color="#333333"
                onClick={verified ? undefined : onClickCaptcha}
              />
              <p className="captcha-text text-sm">{t("I'm not a robot")}</p>
            </div>
            {showCaptcha && (
              <div className="captcha-slider">
                <SliderCaptcha
                  request={onRequest}
                  onVerify={onVerify}
                  bgSize={{ width: 250, height: 150 }}
                  tipText={{
                    default: t('Slide to fill in puzzle'),
                    loading: t('Loading...'),
                  }}
                  autoRequest={false}
                  actionRef={actionRef}
                  loadingBoxProps={{
                    text: t('Loading'),
                  }}
                  style={{
                    backgroundColor: '#F7F9FA',
                  }}
                />
              </div>
            )}
          </div>
          <div className="warning">{t(errorMessage)}</div>
          <TextButton
            text={t('Login')}
            size={TextButtonSize.Medium}
            variant={getWebPageVariant(webPageType).textButtonVariant}
            onClick={onClick}
            disabled={
              !verified ||
              (activeTab === 'email' && email.value.trim().length < 1) ||
              (activeTab === 'phone' && phoneNumber.value.trim().length < 1) ||
              password.value.trim().length < 1
            }
          />
          <div className="footer">
            <Trans i18nKey="Create Account Accept Terms">
              By creating or logging into an account you are agreeing to our
              <a
                className={`text-footer ${
                  getWebPageVariant(webPageType).classVariantLight3
                }`}
                href="https://joyajoy.com/pages/term-of-services"
              >
                Terms and Conditions
              </a>
              and
              <a
                className={`text-footer ${
                  getWebPageVariant(webPageType).classVariantLight3
                }`}
                href="https://joyajoy.com/pages/privacy-policy"
              >
                Privacy Statement
              </a>
            </Trans>
          </div>
          <hr />
          <p className="text-footer-black">{t("Don't have an account?")}</p>
          <TextButton
            text={t('Try Now!')}
            size={TextButtonSize.Medium}
            variant={TextButtonVariant.Orange}
            onClick={onClickTryNow}
          />
          <div className="textLink" onClick={onClickRegister}>
            <SvgIcon
              name={SVGIconType.Register}
              width={20}
              height={20}
              color={'#00A698'}
            />
            {t('Register a New Account')}
            <SvgIcon
              name={SVGIconType.Arrow}
              width={20}
              height={20}
              color={'#00A698'}
            />
          </div>
        </div>
        {isLoading && <LoadingSpinner loading={isLoading} />}
      </div>
    );
  },
);
